import DatePicker from "@mui/lab/DatePicker";
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Iconify from "src/components/Iconify";
import { FormProvider } from "src/components/hook-form";
import UsersSearch from "src/components/autoComplete/users";
import useLocales from "src/hooks/useLocales";
import AmountType from "./components/amountType";
import RowsPerPage from "./components/rowsPerPage";

// Preset coerenti con quelli della KPI Hero Commissions in dashboard.
const PRESETS = [
  { key: "month",   labelKey: "userDashboard.kpi.periodMonth" },
  { key: "quarter", labelKey: "userDashboard.kpi.periodQuarter" },
  { key: "year",    labelKey: "userDashboard.kpi.periodYear" },
  { key: "all",     labelKey: "userDashboard.kpi.periodAll" },
];

const presetToRange = (preset) => {
  const now = dayjs();
  if (preset === "month")   return { from: now.startOf("month"),   to: now };
  if (preset === "quarter") return { from: now.subtract(3, "month"), to: now };
  if (preset === "year")    return { from: now.startOf("year"),    to: now };
  return { from: null, to: null };
};

const defaultValues = {
  start_date: null,
  end_date: null,
  user_id: "",
  payment_type: "",
  rows_page: "25",
};

const FilterCard = ({ fetchData, onExport }) => {
  const { translate } = useLocales();
  const [preset, setPreset] = useState("month");

  const methods = useForm({ defaultValues });
  const { control, setValue, watch, handleSubmit } = methods;

  // Applica il preset selezionato e propaga subito il filtro a chi consuma.
  const applyPreset = (key) => {
    setPreset(key);
    const { from, to } = presetToRange(key);
    setValue("start_date", from);
    setValue("end_date", to);
    const v = watch();
    fetchData({
      payment_type: v.payment_type || "",
      user_id: v.user_id || "",
      start_date: from ? dayjs(from).format("DD/MM/YYYY") : "",
      end_date:   to   ? dayjs(to).format("DD/MM/YYYY")   : "",
    });
  };

  // Inizializzazione: applica il preset "month" e propaga il filtro al primo render.
  useEffect(() => {
    applyPreset("month");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (inputData) => {
    const { start_date, end_date, rows_page, ...rest } = inputData;
    fetchData({
      ...rest,
      start_date: start_date ? dayjs(start_date).format("DD/MM/YYYY") : "",
      end_date:   end_date   ? dayjs(end_date).format("DD/MM/YYYY")   : "",
    });
  };

  const handleExport = () => {
    const v = watch();
    onExport({
      payment_type: v.payment_type,
      user_id: v.user_id,
      start_date: v.start_date ? dayjs(v.start_date).format("DD/MM/YYYY") : "",
      end_date:   v.end_date   ? dayjs(v.end_date).format("DD/MM/YYYY")   : "",
    });
  };

  return (
    <Card sx={{ p: 2.5 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {translate("userFinancial.eWallet.walletHistory")}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              {PRESETS.map((p) => (
                <Chip
                  key={p.key}
                  label={translate(p.labelKey)}
                  size="small"
                  color={preset === p.key ? "primary" : "default"}
                  variant={preset === p.key ? "filled" : "outlined"}
                  onClick={() => applyPreset(p.key)}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={handleExport}
            startIcon={<Iconify icon="mdi:download" sx={{ width: 18, height: 18 }} />}
          >
            {translate("userFinancial.eWallet.exportCsv")}
          </Button>
        </Stack>

        <Box
          sx={{
            display: "grid",
            rowGap: 2,
            columnGap: 2,
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(5, 1fr)",
            },
          }}
        >
          <Controller
            control={control}
            name="start_date"
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                label={translate("userFinancial.eWallet.pickStartDate")}
                inputFormat="dd/MM/yyyy"
                value={field.value}
                onChange={(d) => { field.onChange(d); setPreset("custom"); }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="end_date"
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                inputFormat="dd/MM/yyyy"
                label={translate("userFinancial.eWallet.pickEndDate")}
                value={field.value}
                onChange={(d) => { field.onChange(d); setPreset("custom"); }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            )}
          />
          <UsersSearch
            label={translate("userFinancial.eWallet.users")}
            name="user_id"
            inputProps={{ placeholder: translate("userFinancial.eWallet.typeToSearch") }}
            props={{
              size: "small",
              noOptionsText: translate("userFinancial.eWallet.typeToSearch"),
            }}
          />
          <AmountType />
          <RowsPerPage />
        </Box>

        <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button type="submit" variant="contained" size="small">
            {translate("userFinancial.eWallet.applyFilter")}
          </Button>
        </Box>
      </FormProvider>
    </Card>
  );
};

export default FilterCard;
